import React, { useMemo, useState } from 'react';
import { gamePlatforms, launchGame } from '../../../lib/launchGame/utils';

import { ActionBarProps } from '../../actionBar/ActionBar';
import { CardNotAvailableImage } from '../../cards/components/cardNotAvailableImage';
import { DynamicImage, enLoading } from 'components/Image';
import { ImageProps } from 'next/image';
import { LaunchGameType } from 'lib/launchGame/types';
import Script from 'next/script';
import { callbackOnAuthenticated } from 'utility/auth';
import { getNonce } from 'lib/policy';
import { getSrcImage } from '../../cards/utils';
import { isNotEmpty } from 'utility/functions';
import styles from './QuickSlotCard.module.scss';
import { useCodeGameByWindowSize } from 'hooks/useCodeGameByWindowSize';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';
import { isMatch } from 'utility/functions';
import useWindowSize from 'hooks/useWindowSize';

export type QuickSlotCardProps = {
  id: string;
  title: string;
  slug: string;
  image?: Omit<ImageProps, 'src'> & { srcThumbnail: string; srcCover: string };
  setActiveCardId: React.Dispatch<React.SetStateAction<string | null>>;
  activeCardId: string | null;
  isCover: boolean;
} & Omit<ActionBarProps, 'hasDemo'>;

export const QuickSlotCard = ({
  id,
  title,
  image,
  codicePiattaforma,
  playInfo,
  modalita,
  isInstant,
  setActiveCardId,
  activeCardId,
  isCover,
  gameMode,
  provider,
  idProduttore,
}: QuickSlotCardProps) => {
  const [injectScript, setInjectScript] = useState<boolean>(false);
  const labelGioca = useGetLabelByKey('gioca');
  const [codeGame] = useCodeGameByWindowSize(playInfo?.codeGameDesktop, playInfo?.codeGameMobile);
  const { isMobile } = useWindowSize();

  const handleMouseEnter = (id: string | null): void => {
    setActiveCardId(id);
  };
  const handleMouseLeave = () => {
    setActiveCardId(null);
  };
  const gameScriptSrc = gamePlatforms[codicePiattaforma as keyof typeof gamePlatforms]?.urlParams?.FIM_CONF;

  const { srcImage, hasImg } = useMemo(() => {
    const srcImage = getSrcImage(isCover, image);
    const hasImg = isNotEmpty(srcImage);

    return { srcImage, hasImg };
  }, [isCover, image]);

  const { session, isAuthenticated } = useInternalSession();
  return (
    <React.Fragment>
      {gameScriptSrc && isMatch(gameScriptSrc, '^http') && injectScript && (
        <Script src={gameScriptSrc} nonce={getNonce()} />
      )}
      <button
        className={`${styles.container} ${
          activeCardId && activeCardId === id ? styles.cardIsHovered : styles.cardIsNoHover
        }`}
        onMouseEnter={() => handleMouseEnter(id)}
        onMouseLeave={handleMouseLeave}
        type="button"
        onClick={() =>
          callbackOnAuthenticated(isAuthenticated, () => {
            setInjectScript(true);
            launchGame({
              codicePiattaforma,
              gameCode: codeGame,
              isInstant,
              username: session?.user?.name!,
              cardNumber: session?.user?.cardNumber!,
              isDemo: false,
              modalita,
              provider: provider!,
              gameMode: gameMode!,
              contractCode: session?.user?.contractCode!,
              product: playInfo.product as LaunchGameType['product'],
              dimension: {
                height: playInfo.height,
                width: playInfo.width,
              },
              isMobile,
              idProvider: +idProduttore!,
            });
          })
        }
      >
        <div className={styles.imageContainer}>
          {hasImg ? (
            <DynamicImage src={srcImage} alt={title ?? ''} fill loading={enLoading.lazy} decoding="async" />
          ) : (
            <CardNotAvailableImage />
          )}
        </div>
        <div className={styles.textContainer}>
          <h3 className={styles.label}>{title}</h3>
          <span className={styles.link}>{labelGioca}</span>
        </div>
      </button>
    </React.Fragment>
  );
};
